import { computed, ref, watch } from 'vue';
import moment from 'moment';
import { DISPLAY_DATE_FORMAT } from '../../../common/js/constants/constants';
import { useBranches } from './branches';
import { useTheme } from './theme';
import bookAppointmentApi from '../../../api/bookappointment-api';
import chain from 'ramda/es/chain';
import { useI18n } from 'vue-i18n-composable';

const selectedDate = ref(null);
const selectedTime = ref(null);
const sectionId = ref(null);
const allAppointments = ref({});
const BRANCH_APPOINTMENT_COUNT = 3;
const dates = ref(null);
const times = ref(null);
const { isOpen, selectedBranch, branches } = useBranches();
const { themeId } = useTheme();
const STATES = {
	INIT: Symbol('INIT'),
	LOADING: Symbol('LOADING'),
	NO_TIME: Symbol('NO_TIME'),
	SUCCESS: Symbol('SUCCESS'),
	ERROR: Symbol('ERROR')
};
const APPOINTMENT_STATES = {
	LOADING: Symbol('LOADING'),
	NO_TIME: Symbol('NO_TIME'),
	HAS_TIME: Symbol('HAS_TIME'),
	NO_TIME_LOADING: Symbol('NO_TIME_LOADING')
};
const TOMMOROW = 1;
const THE_DAY_AFTER_TOMORROW = 2;

const state = ref(STATES.INIT);
const isLoading = computed(() => state.value == STATES.LOADING);
const isSuccess = computed(() => state.value == STATES.SUCCESS);
const isNoTime = computed(() => state.value == STATES.NO_TIME);

const setDates = () => {
	const currentAppointment = allAppointments.value[selectedBranch.value.branchCode].data;
	const date = currentAppointment.map((appointment) => {
		const available = !!appointment.times.length && appointment.times.find((time) => time.available);
		const appointmentDay = moment(appointment.day, DISPLAY_DATE_FORMAT.hu);
		const isClosed = !isOpen(selectedBranch.value.branchCode, appointmentDay.isoWeekday() - 1);
		const month = appointmentDay.format('MMMM');
		const day = appointmentDay.format('D');
		const dayName = appointmentDay.format('dddd');
		return { fullDate: appointment.day, available, month, day, dayName, isClosed };
	});
	dates.value = date;
	selectedDate.value = dates.value.find((item) => !item.isClosed && item.available).fullDate;
	sectionId.value = dates.value.find((item) => !item.isClosed && item.available).sectionId;
};
const setTimes = () => {
	const currentAppointment = allAppointments.value[selectedBranch.value.branchCode].data;
	const allTimes = currentAppointment.find((appointment) => appointment.day == selectedDate.value).times;
	const morning = allTimes.filter((time) => moment(time.startTime, 'HH:mm').isBefore(moment('12:00', 'HH:mm')));
	const afternoon = allTimes.filter((time) => moment(time.startTime, 'HH:mm').isSameOrAfter(moment('12:00', 'HH:mm')));
	times.value = { morning, afternoon };
};
const getAppointments = async (branchCode) => {
	const appointments = allAppointments.value[branchCode];
	return new Promise(async (resolve, reject) => {
		if (appointments && !appointments.loading) {
			state.value = STATES.SUCCESS;
			return resolve(appointments);
		} else {
			try {
				state.value = STATES.LOADING;
				allAppointments.value = { ...allAppointments.value, [branchCode]: { loading: true } };
				const { data } = await bookAppointmentApi.getAppointmentList(themeId.value, branchCode);
				const appointmentData = { data, loading: false };
				allAppointments.value = { ...allAppointments.value, [branchCode]: appointmentData };
				resolve(appointmentData);
			} catch (err) {
				state.value = STATES.ERROR;
				reject(err);
			}
		}
	});
};
const getState = (index) => {
	if (branches.value[index]) {
		const key = branches.value[index].branchCode;
		const appointments = allAppointments.value[key];
		const getTimes = chain((date) => date.times);
		if (appointments?.data) {
			const allTimes = getTimes(appointments.data);
			return appointments.data.length && allTimes.length ? APPOINTMENT_STATES.HAS_TIME : APPOINTMENT_STATES.NO_TIME;
		} else if (appointments?.loading) {
			return APPOINTMENT_STATES.LOADING;
		} else {
			return APPOINTMENT_STATES.NO_TIME_LOADING;
		}
	}
};

const getFirstAvailable = (index) => {
	const { t } = useI18n();
	if (branches.value[index]) {
		const key = branches.value[index].branchCode;
		const appointments = allAppointments.value[key];
		const firstAvailable = appointments.data.find((item) => item.times.length && item.times.find((time) => time.available));
		const firstAppointment = firstAvailable.times.find((time) => time.available).startTime;
		const today = moment();
		const firstDate = moment(firstAvailable.day, 'YYYY.MM.DD');
		const diff = firstDate.diff(today, 'days') + 1;
		if (diff == TOMMOROW) {
			return `${t('tomorrow')}, ${firstAppointment}`;
		} else if (diff == THE_DAY_AFTER_TOMORROW) {
			return `${t('day-after-tomorrow')}, ${firstAppointment}`;
		}
		return moment(`${firstAvailable.day} ${firstAppointment}`, 'YYYY.MM.DD HH:mm').format('MMMM D., dddd, HH:mm');
	}
};

const hasTimes = (index) => getState(index) == APPOINTMENT_STATES.HAS_TIME;
const noTimes = (index) => getState(index) == APPOINTMENT_STATES.NO_TIME;
const isTimeLoading = (index) => getState(index) == APPOINTMENT_STATES.LOADING;
const noTimeLoading = (index) => getState(index) == APPOINTMENT_STATES.NO_TIME_LOADING;

const isDayDisabled = (date) => !date.available || date.isClosed;
const isTimeDisabled = (time) => !time.available;

const getFirstTime = computed(
	() =>
		(times.value && times.value.morning.find((time) => !isTimeDisabled(time))) ||
		times.value.afternoon.find((time) => !isTimeDisabled(time))
);
const getFirstTimeIndex = computed(() => [...times.value.morning, ...times.value.afternoon].findIndex((time) => !isTimeDisabled(time)));

watch(selectedBranch, async (val) => {
	if (!val) return;
	const appointments = await getAppointments(val.branchCode);
	const getTimes = chain((date) => date.times);
	const allTimes = getTimes(appointments.data);
	if (appointments?.data?.length && allTimes?.length) {
		state.value = STATES.SUCCESS;
		setDates();
		setTimes();
	} else {
		state.value = STATES.NO_TIME;
	}
});
watch(selectedDate, (val) => {
	if (val) {
		setTimes();
	}
});
watch(branches, () => {
	const num = Math.min(branches.value.length, BRANCH_APPOINTMENT_COUNT);
	for (let i = 0; i < num; i++) {
		getAppointments(branches.value[i].branchCode);
	}
});
export const useAppointments = () => {
	const { locale } = useI18n();
	const getSelectedDate = () => selectedDate;
	const getSelectedTime = () => selectedTime;
	const getSectionId = () => sectionId;

	const appointment = computed(() =>
		moment(`${selectedDate.value} ${selectedTime.value}`, 'YYYY.MM.DD HH:mm').format(
			locale.value === 'hu' ? 'YYYY. MMMM D., dddd, HH:mm' : 'dddd, MMMM D, YYYY, HH:mm'
		)
	);
	const appointmentBackendFormat = computed(() => moment(`${selectedDate.value} ${selectedTime.value}`, 'YYYY.MM.DD HH:mm').format());

	const resetAppointment = () => {
		selectedDate.value = dates.value.find((item) => !item.isClosed && item.available).fullDate;
		selectedTime.value = null;
	};

	const getInitPage = (mobile = true) => {
		return Math.ceil((dates.value.map((e) => e.fullDate).indexOf(selectedDate.value) + 1) / (mobile ? 3 : 7));
	};

	const getFirstAvailableIndexInRange = (pageNo, isMobile, backwards = false) => {
		const firstIndex = isMobile ? (pageNo - 1) * 3 : (pageNo - 1) * 7;
		const lastIndex = isMobile ? pageNo * 3 - 1 : pageNo * 7 - 1;
		const indexes = dates.value.reduce((arr, date, currentIndex) => {
			if (currentIndex >= firstIndex && currentIndex <= lastIndex && !date.isClosed && date.available) {
				arr.push(currentIndex);
			}
			return arr;
		}, []);

		return backwards ? indexes[indexes.length - 1] : indexes[0];
	};

	return {
		dates,
		times,
		appointment,
		appointmentBackendFormat,
		isLoading,
		isSuccess,
		isNoTime,
		getFirstTime,
		getFirstTimeIndex,
		hasTimes,
		noTimes,
		isTimeLoading,
		noTimeLoading,
		getSelectedDate,
		getSelectedTime,
		getSectionId,
		resetAppointment,
		getFirstAvailable,
		isDayDisabled,
		isTimeDisabled,
		getInitPage,
		getFirstAvailableIndexInRange
	};
};
